import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  Quote,
  ArrowLeft,
  ArrowRight,
  MessageCircle,
} from "lucide-react";


const testimonials = [
  {
    id: 1,
    name: "Rahul Sharma",
    role: "CEO, TechNova Solutions",
    initials: "RS",
    rating: 5,
    review:
      "OjMee Technology delivered an outstanding website and mobile app for our business. Their team was professional, creative and always responsive. Highly recommended!",
  },
  {
    id: 2,
    name: "Priya Mehta",
    role: "Founder, BloomMart",
    initials: "PM",
    rating: 5,
    review:
      "We partnered with OjMee Technology for our eCommerce platform, and the results were beyond our expectations. The system is fast, secure and user-friendly.",
  },
  {
    id: 3,
    name: "Amit Verma",
    role: "Operations Head, GlobalTech",
    initials: "AV",
    rating: 5,
    review:
      "The team at OjMee Technology built our custom ERP software exactly as we needed. Their communication, technical expertise and support were excellent.",
  },
  {
    id: 4,
    name: "Neha Kapoor",
    role: "Business Owner",
    initials: "NK",
    rating: 5,
    review:
      "The team understood our requirements and delivered a clean, modern and reliable solution. We are very happy with the overall development experience.",
  },
  {
    id: 5,
    name: "Vikash Gupta",
    role: "Product Manager",
    initials: "VG",
    rating: 5,
    review:
      "From planning to deployment, OjMee Technology provided excellent technical guidance. Their solutions helped us improve our workflow and productivity.",
  },
  {
    id: 6,
    name: "Anjali Singh",
    role: "Founder, Digital Business",
    initials: "AS",
    rating: 5,
    review:
      "A reliable technology partner with a creative approach. The website performance, design quality and support have been impressive.",
  },
];


export default function CustomerFeedback() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const visibleTestimonials = testimonials.slice(
    currentIndex,
    currentIndex + 3
  );

  const nextSlide = () => {
    setCurrentIndex((previousIndex) => {
      return previousIndex === 0 ? 3 : 0;
    });
  };

  const previousSlide = () => {
    setCurrentIndex((previousIndex) => {
      return previousIndex === 0 ? 3 : 0;
    });
  };

  return (
    <section className="relative w-full overflow-hidden px-4 py-10 sm:px-6">

      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[500px] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-600/10 blur-[140px]" />

      <div className="pointer-events-none absolute -left-40 top-20 -z-10 h-72 w-72 rounded-full bg-violet-600/10 blur-[120px]" />

      <div className="mx-auto max-w-7xl">

        <div className="mx-auto mb-12 max-w-3xl text-center">

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-400/20 bg-pink-400/5 px-4 py-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-200">
              Customer Feedback
            </span>
          </div>

          <h2 className="text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl">
            What Our{" "}
            <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              Clients Say
            </span>
          </h2>

          <p className="mt-5 text-sm leading-7 text-slate-400 sm:text-base">
            Real experiences from businesses that trusted OjMee Technology
            to transform their ideas into powerful digital solutions.
          </p>

        </div>

        <div className="relative mx-auto max-w-7xl">

          <button
            type="button"
            onClick={previousSlide}
            aria-label="Previous testimonials"
            className="absolute left-0 top-1/2 z-20 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-pink-400/30 bg-[#120b2c] text-pink-300 shadow-xl transition-all duration-300 hover:scale-110 hover:bg-pink-500/20 lg:flex"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 gap-6 md:grid-cols-3"
            >

              {visibleTestimonials.map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="relative overflow-hidden rounded-[2rem] border border-pink-400/20 bg-gradient-to-br from-pink-500/10 via-violet-700/10 to-white/[0.04] p-7 shadow-[0_30px_100px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-8"
                >

                  <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-pink-500/15 blur-[90px]" />

                  <div className="relative z-10">

                    <div className="flex items-center gap-4">

                      <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-pink-500 to-violet-700 text-sm font-black text-white shadow-lg shadow-pink-500/20">
                        {testimonial.initials}
                      </div>

                      <div className="min-w-0">

                        <h3 className="truncate text-base font-black text-white">
                          {testimonial.name}
                        </h3>

                        <p className="mt-1 truncate text-xs text-slate-400">
                          {testimonial.role}
                        </p>

                      </div>

                    </div>

                    <div className="mt-5 flex w-fit items-center gap-1 rounded-full border border-yellow-400/10 bg-yellow-400/5 px-3 py-2">

                      {Array.from({
                        length: testimonial.rating,
                      }).map((_, index) => (
                        <Star
                          key={index}
                          className="h-4 w-4 fill-yellow-400 text-yellow-400"
                        />
                      ))}

                    </div>

                    <div className="mt-7">

                      <Quote className="h-10 w-10 fill-pink-400/10 text-pink-400/70" />

                    </div>

                    <p className="mt-4 min-h-[175px] text-sm font-medium leading-7 text-slate-200 sm:text-base">
                      "{testimonial.review}"
                    </p>

                    <div className="mt-6 border-t border-white/10 pt-5">

                      <div className="flex items-center justify-between gap-3">

                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">

                          <MessageCircle className="h-4 w-4 text-pink-300" />

                          Customer Experience

                        </div>

                        <div className="text-[10px] font-bold uppercase tracking-wider text-pink-300">
                          Verified
                        </div>

                      </div>

                    </div>

                  </div>

                </motion.div>
              ))}

            </motion.div>
          </AnimatePresence>

          <button
            type="button"
            onClick={nextSlide}
            aria-label="Next testimonials"
            className="absolute right-0 top-1/2 z-20 hidden h-12 w-12 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-violet-400/30 bg-[#120b2c] text-violet-300 shadow-xl transition-all duration-300 hover:scale-110 hover:bg-violet-500/20 lg:flex"
          >
            <ArrowRight className="h-5 w-5" />
          </button>

          <div className="mt-7 flex items-center justify-center gap-5 lg:hidden">

            <button
              type="button"
              onClick={previousSlide}
              aria-label="Previous testimonials"
              className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.05] text-slate-300 transition hover:border-pink-400/40 hover:text-pink-300"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <span className="text-xs font-bold text-slate-500">
              {currentIndex === 0 ? "01 - 03" : "04 - 06"}
            </span>

            <button
              type="button"
              onClick={nextSlide}
              aria-label="Next testimonials"
              className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.05] text-slate-300 transition hover:border-violet-400/40 hover:text-violet-300"
            >
              <ArrowRight className="h-4 w-4" />
            </button>

          </div>

          <div className="mt-7 flex items-center justify-center gap-2">

            {[0, 3].map((index) => (
              <button
                key={index}
                type="button"
                aria-label={
                  index === 0
                    ? "Show testimonials 1 to 3"
                    : "Show testimonials 4 to 6"
                }
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentIndex === index
                    ? "w-8 bg-gradient-to-r from-pink-400 to-violet-500"
                    : "w-2 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}

          </div>

        </div>

      </div>
    </section>
  );
}